import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Plus, Puzzle } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EmptyState, StatusBadge } from "@/components/admin/ui";
import { getSolutionIcon } from "@/lib/solutionIcons";
import AddSolutionModal from "@/components/admin/solutions/AddSolutionModal";
import { usePolling } from "@/hooks/usePolling";
import { usePageLastUpdated } from "@/context/PageMetaContext";

export default function AdminSolutions() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [solutions, setSolutions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    if (!silent) setError("");
    try {
      const { data } = await axios.get(`${API}/admin/solutions`, { withCredentials: true });
      setSolutions(data.solutions || []);
      setSummary(data.summary || null);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  usePolling(() => load(true), 10000);

  usePageLastUpdated(lastUpdated, "solutions-last-updated");

  return (
    <div className="space-y-6" data-testid="admin-solutions-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-xl text-sm text-slate-500 dark:text-slate-400">Manage the Nova Opsis capabilities available to organizations.</p>
        <button type="button" onClick={() => setShowAdd(true)} data-testid="add-solution-btn" className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#0052FF] to-[#7B61FF] px-5 py-2.5 text-sm font-bold text-white">
          <Plus size={16} /> Add Solution
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-4 dark:border-white/10 dark:bg-[#111C33]" data-testid="solutions-summary">
          <SummaryStat label="Total Solutions" value={summary.total} testId="summary-total" />
          <SummaryStat label="Active Solutions" value={summary.active} testId="summary-active" />
          <SummaryStat label="Draft Solutions" value={summary.draft} testId="summary-draft" />
          <SummaryStat label="Organizations Assigned" value={summary.organizations_assigned} testId="summary-orgs" />
        </div>
      )}

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="solutions-error">{error}</p>}

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="solutions-loading">Loading solutions…</div>
      ) : solutions.length === 0 ? (
        <EmptyState icon={Puzzle} title="No solutions configured." message="Register your first Nova Opsis Solution to get started." testId="solutions-empty" />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="solutions-grid">
          {solutions.map((s) => {
            const Icon = getSolutionIcon(s.icon);
            return (
              <div key={s.solution_id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-[#111C33]" data-testid={`solution-card-${s.slug}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-500/10"><Icon size={20} className="text-[#0052FF] dark:text-blue-400" /></span>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-slate-950 dark:text-white">{s.name}</p>
                      {s.is_core && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:bg-violet-500/10 dark:text-violet-400">Core</span>}
                    </div>
                    <StatusBadge status={s.status} testId={`solution-status-${s.slug}`} />
                  </div>
                </div>
                <p className="mt-3 flex-1 text-sm text-slate-600 dark:text-slate-400">{s.description}</p>
                <div className="mt-4 flex items-center gap-5 text-sm text-slate-500 dark:text-slate-400">
                  <span data-testid={`solution-org-count-${s.slug}`}>{s.organizations_count} Organizations</span>
                  <span data-testid={`solution-user-count-${s.slug}`}>{s.users_with_access} Users</span>
                </div>
                <button type="button" onClick={() => navigate(`/admin/solutions/${s.solution_id}`)} data-testid={`solution-manage-${s.slug}`} className="mt-5 rounded-xl border border-slate-200 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">Manage</button>
              </div>
            );
          })}
        </div>
      )}

      <AddSolutionModal open={showAdd} onClose={() => setShowAdd(false)} onCreated={load} API={API} />
    </div>
  );
}

const SummaryStat = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <p className="text-3xl font-extrabold text-slate-950 dark:text-white">{value}</p>
    <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">{label}</p>
  </div>
);
